import { Editor as Tinymce } from '@tinymce/tinymce-react';
import { bool, func, string } from 'prop-types';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import contentCss from 'tinymce/skins/content/default/content.min.css';
import contentUICss from 'tinymce/skins/ui/oxide/content.min.css';
const StyledEditor = styled.div`
   max-width: 100%;
   width: 100%;
   &[data-error='true'] {
      & .tox-tinymce {
         border-color: #e41d32;
      }
   }
   &[data-focused='true'] {
      & .tox-tinymce {
         border: 1.5px solid var(--main);
      }
   }
   & .tox-edit-area__iframe {
      background-color: #ffffff;
      .dark & {
         background-color: #2e2d2d;
      }
   }
   & .tox-tinymce {
      border-radius: 12px;
      border: 1.5px solid #e1e1e1;
      height: 450px !important;
      .dark & {
         border: 1.5px solid #555555;
      }
   }
   & .tox {
      & .tox-editor-header {
         z-index: initial;
         background-color: #ffffff;
         .dark & {
            background-color: #2e2d2d;
         }
         & .tox-menubar {
            background-color: transparent;
         }
         .dark & .tox-menu {
            background-color: #2e2d2d !important;
            color: #ffffff;
         }
         & .tox-toolbar {
            background-color: transparent;
         }
         & button {
            font-family: 'Onest', sans-serif !important;
         }
         & .tox-mbtn {
            cursor: pointer;
            &:hover,
            &:focus {
               background-color: #f7f8fc !important;
            }
         }
         .dark & .tox-mbtn {
            &:hover,
            &:focus {
               background-color: #222222 !important;
               color: #ffffff;
            }
         }
         & .tox-mbtn--active {
            background-color: #f7f8fc !important;
         }
         .dark & .tox-mbtn--active {
            background-color: #222222 !important;
            color: #ffffff;
         }
         & .tox-mbtn__select-label {
            color: #696f85;
            cursor: pointer;
            font-size: 15px;
            font-weight: 500;
         }
         .dark & .tox-mbtn__select-label {
            color: #ffffff;
         }
         & .tox-toolbar__group {
            border: none !important;
         }
      }
      & .tox-tbtn--bespoke {
         & .tox-tbtn__select-label {
            color: #696f85;
            cursor: pointer;
            font-size: 15px;
            font-weight: 500;
            width: initial !important;
         }
         .dark & .tox-tbtn__select-label {
            color: #e1e1e1;
         }
      }
      & .tox-tbtn--enabled {
         background-color: #f7f8fc;
      }
      .dark & .tox-tbtn--enabled {
         background-color: #222222;
         color: #ffffff;
      }
      & .tox-tbtn {
         cursor: pointer !important;
         &:hover {
            background-color: #f7f8fc;
         }
         & svg {
            fill: #696f85 !important;
         }
         .dark & svg {
            fill: #ffffff !important;
         }
      }
      .dark & .tox-tbtn,
      .dark & .tox-split-button {
         &:hover,
         &:focus {
            background-color: #222222 !important;
            color: #ffffff;
         }
      }
      & .tox-statusbar {
         display: none;
      }
   }
`;
const init = {
   content_css: 'dark', // Dark content
   height: 450,
   menubar: 'file edit view insert format table tools help',
   paste_as_text: true,
   plugins:
      'paste codesample advlist autolink link image lists charmap anchor spellchecker searchreplace wordcount code fullscreen insertdatetime media nonbreaking table template help',
   skin: 'oxide-dark',
   toolbar_mode: 'wrap',
   toolbar: [
      {
         items: ['undo', 'redo'],
         name: 'history',
      },
      {
         items: ['fontselect'],
         name: 'fontselect',
      },
      {
         className: 'fontsizeselect',
         items: ['fontsizeselect'],
         name: 'fontsizeselect',
      },
      {
         items: ['bold', 'italic', 'underline', 'strikethrough'],
         name: 'formatting',
      },
      {
         items: ['outdent', 'indent'],
         name: 'indentation',
      },
      {
         items: ['alignleft', 'aligncenter', 'alignright', 'alignjustify'],
         name: 'alignment',
      },
      {
         items: ['numlist', 'bullist', 'checklist'],
         name: 'list',
      },
      {
         items: ['table'],
         name: 'table',
      },
      {
         items: [
            'forecolor',
            'backcolor',
            'casechange',
            'permanentpen',
            'formatpainter',
            'removeformat',
         ],
         name: 'colors',
      },
      {
         items: ['subscript', 'superscript'],
         name: 'math',
      },
      {
         items: ['charmap'],
         name: 'chars',
      },
      {
         items: ['image', 'link'],
         name: 'file',
      },
   ],
};
const Editor = memo(
   ({ isDisabled = false, isError = false, onChange, onFocus, value = '' }) => {
      const { i18n } = useTranslation();
      const [isFocused, setIsFocused] = useState(false);
      const { language } = i18n;
      return (
         <StyledEditor
            data-focused={isFocused}
            className='dark'
            data-error={isError}
         >
            <Tinymce
               disabled={isDisabled}
               onBlur={() => setIsFocused(false)}
               onEditorChange={onChange}
               value={value}
               onFocus={e => {
                  if (typeof onFocus === 'function') {
                     onFocus(e);
                  }
                  setIsFocused(true);
               }}
               init={{
                  ...init,
                  content_style: [contentCss, contentUICss].join('\n'),
                  language,
               }}
            />
         </StyledEditor>
      );
   },
);
Editor.propTypes = {
   isDisabled: bool,
   isError: bool,
   onChange: func,
   onFocus: func,
   value: string,
};
export default Editor;
